<template>
  <div class="layout-container">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ open: isSidebarOpen }" />
    
    <div class="main-content">
      <div class="tree-view-courses">
        <div v-if="loading" class="loading">
          Loading instructors...
        </div>
        <div v-else-if="error" class="error">
          {{ error }}
        </div>
        <div v-else class="courses-tree">
          <div class="tree-header">
            <h2>Instructors</h2>
            <div class="stats">
              Total Instructors: {{ instructors.length }}
            </div>
          </div>
          <div class="tree-content">
            <div v-for="instructor in instructors" :key="instructor.id" class="course-item">
              <div class="course-header" @click="toggleInstructor(instructor.id)">
                <i :class="expandedInstructors.includes(instructor.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                <span class="course-title">{{ instructor.title }}</span>
                <span class="course-status" :class="instructor.status">{{ instructor.status }}</span>
              </div>
              <div v-if="expandedInstructors.includes(instructor.id)" class="course-details">
                <div class="detail-row">
                  <span class="label">Description:</span>
                  <p class="value">{{ instructor.description }}</p>
                </div>
                <div class="detail-row">
                  <span class="label">Session Duration:</span>
                  <span class="value">{{ instructor.minutes }} minutes</span>
                </div>
                <div class="detail-row">
                  <span class="label">Reviews:</span>
                  <span class="value">{{ instructor.students_count }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Rating:</span>
                  <span class="value">{{ instructor.average_rating || 'Not rated' }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Session Price:</span>
                  <span class="value">{{ instructor.price ? `$${instructor.price}` : 'Free' }}</span>
                </div>
                
                <!-- Instructor's Courses -->
                <div v-if="instructorCourses[instructor.id]" class="instructor-courses">
                  <h3>Courses</h3>
                  <div v-for="course in instructorCourses[instructor.id]" :key="course.id" class="nested-course-item">
                    <div class="nested-course-header" @click="toggleCourse(course.id)">
                      <div class="course-title-wrapper">
                        <i :class="expandedCourses.includes(course.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                        <span class="nested-course-title">{{ course.title }}</span>
                      </div>
                      <span class="course-status" :class="course.status">{{ course.status }}</span>
                    </div>
                    <div v-if="expandedCourses.includes(course.id)" class="nested-course-details">
                      <div class="detail-row">
                        <span class="label">Description:</span>
                        <p class="value">{{ course.description }}</p>
                      </div>
                      <div class="detail-row">
                        <span class="label">Price:</span>
                        <span class="value">{{ course.price ? `$${course.price}` : 'Free' }}</span>
                      </div>
                      
                      <!-- Course Lessons -->
                      <div v-if="course.lessons && course.lessons.length > 0" class="course-lessons">
                        <h4>Lessons ({{ course.lessons.length }})</h4>
                        <div v-for="lesson in course.lessons" :key="lesson.id" class="lesson-item">
                          <div class="lesson-header">
                            <span class="lesson-title">{{ lesson.title }}</span>
                            <span class="lesson-duration">{{ Math.round(lesson.duration / 60) }} min</span>
                          </div>
                          <p v-if="lesson.description" class="lesson-description">{{ lesson.description }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import Sidebar from './Sidebar.vue'

export default {
  name: 'TreeViewCourses',
  components: {
    Sidebar
  },
  setup() {
    const instructors = ref([])
    const instructorCourses = ref({}) // Map of instructor ID to their courses
    const loading = ref(true)
    const error = ref(null)
    const expandedInstructors = ref([])
    const expandedCourses = ref([]) // Track expanded courses
    const isSidebarOpen = ref(false)

    const fetchInstructors = async () => {
      const token = localStorage.getItem('token')
      if (!token) {
        error.value = 'Authentication token not found. Please log in again.'
        loading.value = false
        return
      }

      try {
        const config = {
          method: 'get',
          url: 'https://api.kewaar.com/api/admin/courses/instructors/all',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          timeout: 10000
        }
        const response = await axios.request(config)
        
        if (!response.data?.success) {
          throw new Error('API request was not successful')
        }
        
        if (!response.data?.data?.data) {
          throw new Error(`Invalid response structure. Expected data.data.data but got: ${JSON.stringify(response.data)}`)
        }
        
        if (!Array.isArray(response.data.data.data)) {
          throw new Error(`Expected data.data.data to be an array but got: ${typeof response.data.data.data}`)
        }
        
        instructors.value = response.data.data.data.map(instructor => ({
          id: instructor.id,
          user_id: instructor.user_id,
          title: `${instructor.first_name} ${instructor.last_name}`,
          description: instructor.bio || 'No bio available',
          minutes: instructor.session_duration || 0,
          students_count: instructor.total_reviews || 0,
          average_rating: instructor.rating || null,
          price: instructor.price_per_session || null,
          is_premium: Boolean(instructor.is_available),
          courses: instructor.courses || []
        }))

        // Pre-populate instructor courses
        instructorCourses.value = response.data.data.data.reduce((acc, instructor) => {
          acc[instructor.id] = instructor.courses.map(course => ({
            id: course.id,
            title: course.title || 'Untitled Course',
            description: course.description || 'No description available',
            price: course.price || null,
            status: course.status || 'active',
            is_premium: course.is_premium || false
          }))
          return acc
        }, {})
      } catch (err) {
        console.error('Error fetching instructors:', err)
        handleError(err)
      } finally {
        loading.value = false
      }
    }

    const fetchInstructorCourses = async (instructorId) => {
      // If we already have the courses, no need to fetch again
      if (instructorCourses.value[instructorId]) return

      const token = localStorage.getItem('token')
      if (!token) {
        error.value = 'Authentication token not found. Please log in again.'
        return
      }

      try {
        const config = {
          method: 'get',
          url: `https://api.kewaar.com/api/admin/instructors/${instructorId}/courses`,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          timeout: 10000
        }
        const response = await axios.request(config)
        
        if (!response.data?.data) {
          throw new Error('Invalid response format')
        }
        
        instructorCourses.value[instructorId] = response.data.data.map(course => ({
          id: course.id,
          title: course.title || 'Untitled Course',
          description: course.description || 'No description available',
          price: course.price || null,
          status: course.status || 'active',
          is_premium: course.is_premium || false
        }))
      } catch (err) {
        console.error('Error fetching instructor courses:', err)
        handleError(err)
      }
    }

    const handleError = (err) => {
      if (err.response) {
        switch (err.response.status) {
          case 401:
            error.value = 'Session expired. Please log in again.'
            break
          case 403:
            error.value = 'You do not have permission to view these resources.'
            break
          case 404:
            error.value = 'Resource not found.'
            break
          default:
            error.value = `Failed to fetch data: ${err.response.data?.message || err.message}`
        }
      } else if (err.request) {
        error.value = 'Network error. Please check your connection.'
      } else {
        error.value = `Failed to fetch data: ${err.message}`
      }
    }

    const toggleInstructor = async (instructorId) => {
      const index = expandedInstructors.value.indexOf(instructorId)
      if (index === -1) {
        expandedInstructors.value.push(instructorId)
        await fetchInstructorCourses(instructorId)
      } else {
        expandedInstructors.value.splice(index, 1)
      }
    }

    const toggleCourse = async (courseId) => {
      const index = expandedCourses.value.indexOf(courseId)
      if (index === -1) {
        expandedCourses.value.push(courseId)
      } else {
        expandedCourses.value.splice(index, 1)
      }
    }

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value
    }

    onMounted(() => {
      fetchInstructors()
    })

    return {
      instructors,
      instructorCourses,
      loading,
      error,
      expandedInstructors,
      expandedCourses,
      toggleInstructor,
      toggleCourse,
      isSidebarOpen,
      toggleSidebar
    }
  }
}
</script>

<style scoped>
.tree-view-courses {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin: 20px;
}

.loading, .error {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
}

.loading {
  color: #4a5568;
}

.error {
  color: #e53e3e;
  background: #fff5f5;
  border-radius: 8px;
  border: 1px solid #fed7d7;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #edf2f7;
}

.tree-header h2 {
  margin: 0;
  color: #2d3748;
  font-size: 1.5em;
  font-weight: 600;
}

.stats {
  color: #4a5568;
  font-size: 0.95em;
  background: #f7fafc;
  padding: 8px 16px;
  border-radius: 6px;
}

.course-item {
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.course-item:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.course-header {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  background: #f8fafc;
  transition: background-color 0.2s;
}

.course-header:hover {
  background: #edf2f7;
}

.course-header i {
  margin-right: 12px;
  color: #4a5568;
  transition: transform 0.2s;
}

.course-title {
  flex: 1;
  font-weight: 600;
  color: #2d3748;
  font-size: 1.1em;
}

.course-status {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  font-weight: 500;
  text-transform: capitalize;
}

.course-status.active {
  background: #48bb78;
  color: white;
}

.course-details {
  padding: 20px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.detail-row {
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: #4a5568;
  margin-right: 12px;
  min-width: 120px;
}

.value {
  color: #2d3748;
  flex: 1;
}

.value p {
  margin: 8px 0;
  line-height: 1.5;
  color: #4a5568;
}

.instructor-courses {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #edf2f7;
}

.instructor-courses h3 {
  margin: 0 0 20px 0;
  color: #2d3748;
  font-size: 1.2em;
  font-weight: 600;
}

.nested-course-item {
  margin: 12px 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.nested-course-item:hover {
  background: #edf2f7;
  transform: translateX(4px);
}

.nested-course-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  cursor: pointer;
  padding: 12px;
  transition: background-color 0.2s;
}

.nested-course-header:hover {
  background: #e2e8f0;
}

.course-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.course-title-wrapper i {
  color: #4a5568;
  width: 14px;
  transition: transform 0.2s;
}

.nested-course-title {
  font-weight: 600;
  color: #2d3748;
  font-size: 1.05em;
}

.nested-course-details {
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.course-lessons {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #edf2f7;
}

.course-lessons h4 {
  margin: 0 0 16px 0;
  color: #2d3748;
  font-size: 1.1em;
  font-weight: 600;
}

.lesson-item {
  padding: 12px;
  margin: 8px 0;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.lesson-item:hover {
  background: #edf2f7;
  transform: translateX(4px);
}

.lesson-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.lesson-title {
  font-weight: 600;
  color: #2d3748;
}

.lesson-duration {
  font-size: 0.9em;
  color: #4a5568;
  background: #edf2f7;
  padding: 4px 8px;
  border-radius: 4px;
}

.lesson-description {
  margin: 8px 0 0 0;
  font-size: 0.95em;
  color: #4a5568;
  line-height: 1.5;
}

.layout-container {
  display: flex;
  min-height: 100vh;
  background: #f7fafc;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
  padding: 20px;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 16px;
  }
  
  .main-content.sidebar-open {
    margin-left: 280px;
  }

  .tree-view-courses {
    margin: 10px;
    padding: 16px;
  }

  .detail-row {
    flex-direction: column;
    gap: 8px;
  }

  .label {
    min-width: auto;
  }
}
</style> 